import { logger } from "./logger";

export class AppError extends Error {
  public readonly statusCode: number;
  public readonly code: string;

  constructor(message: string, statusCode: number, code: string) {
    super(message);
    this.name = this.constructor.name;
    this.statusCode = statusCode;
    this.code = code;
    Error.captureStackTrace(this, this.constructor);
  }
}

export class UnauthorizedError extends AppError {
  constructor(message = "You must be signed in") {
    super(message, 401, "UNAUTHORIZED");
  }
}

export class ForbiddenError extends AppError {
  constructor(message = "You do not have permission to perform this action") {
    super(message, 403, "FORBIDDEN");
  }
}

export class NotFoundError extends AppError {
  constructor(message = "Resource not found") {
    super(message, 404, "NOT_FOUND");
  }
}

export class ValidationError extends AppError {
  constructor(message = "Validation failed") {
    super(message, 400, "VALIDATION_ERROR");
  }
}

export class TenantAccessError extends AppError {
  constructor(message = "Access denied: resource belongs to a different organization") {
    super(message, 403, "TENANT_ACCESS_DENIED");
  }
}

export class ModuleNotEnabledError extends AppError {
  constructor(moduleId: string) {
    super(`Module "${moduleId}" is not enabled for this organization`, 403, "MODULE_NOT_ENABLED");
  }
}

export class InvalidOrderTransitionError extends AppError {
  constructor(from: string, to: string) {
    super(`Cannot transition order from ${from} to ${to}`, 400, "INVALID_ORDER_TRANSITION");
  }
}

export class CashDrawerClosedError extends AppError {
  constructor() {
    super("Cash drawer is not open", 400, "CASH_DRAWER_CLOSED");
  }
}

export class PaymentProcessingError extends AppError {
  constructor(message = "Payment processing failed") {
    super(message, 400, "PAYMENT_PROCESSING_ERROR");
  }
}

export class DuplicateOrderError extends AppError {
  constructor(orderNumber: string) {
    super(`Order ${orderNumber} already exists`, 409, "DUPLICATE_ORDER");
  }
}

export function errorResponse(error: unknown): Response {
  if (error instanceof AppError) {
    return Response.json(
      { error: error.message, code: error.code },
      { status: error.statusCode },
    );
  }

  logger.error("Unhandled error", {
    error:
      error instanceof Error
        ? { name: error.name, message: error.message, stack: error.stack }
        : String(error),
  });

  return Response.json(
    { error: "An unexpected error occurred", code: "INTERNAL_ERROR" },
    { status: 500 },
  );
}
